/**
 * Der Stammplatz eines Artikels oder Packstücks, zum Ansehen und Ändern
 * (ADR 0013 §6).
 *
 * Ändern braucht Netz -- der Stammplatz ist ein Stammdatum (ADR 0006). Ohne
 * Netz ist der Knopf gesperrt und sagt, warum, statt dass eine Änderung
 * scheinbar klappt und später verschwindet.
 */

import { amStammplatz } from "@thw/core-domain";
import type { Bestand } from "../lokal/bestand.js";
import { stammplatzVon } from "../lokal/stammplatz.js";
import { ortZusatz } from "./Wohin.jsx";
import { Knopf } from "./bausteine.jsx";

/** „T7-F1 — Fach 1 (in HALLE)“ -- ein Stammplatz in Worten. */
export function stammplatzText(bestand: Bestand, lagerort_id: string): string {
  const l = bestand.lagerorte.get(lagerort_id);
  if (l === undefined) return "unbekannter Lagerort";
  const zusatz = ortZusatz(bestand, l);
  const name = l.standardziel ? l.bezeichnung : `${l.kennung} — ${l.bezeichnung}`;
  return zusatz === null ? name : `${name} (${zusatz})`;
}

/** Ist der Artikel nicht an seinem Stammplatz? `false` ohne Stammplatz. */
export function nichtAmStammplatz(bestand: Bestand, artikel_id: string): boolean {
  const stammplatz = stammplatzVon(bestand, artikel_id);
  return stammplatz !== null && !amStammplatz(bestand.zustaende.get(artikel_id), stammplatz);
}

export function StammplatzZeile({
  bestand,
  artikel_id,
  online,
  aufAendern,
  aufEntfernen,
}: {
  bestand: Bestand;
  artikel_id: string;
  online: boolean;
  aufAendern: () => void;
  aufEntfernen: () => void;
}) {
  const stammplatz = stammplatzVon(bestand, artikel_id);
  return (
    <div
      data-testid="stammplatz"
      className="mt-2 flex flex-wrap items-center justify-between gap-2"
    >
      <div className="min-w-0">
        <p className="text-sm font-semibold text-neutral-500">Stammplatz</p>
        <p className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">
            {stammplatz === null ? "keiner" : stammplatzText(bestand, stammplatz)}
          </span>
          {nichtAmStammplatz(bestand, artikel_id) && (
            <span className="rounded-full bg-amber-400 px-2.5 py-0.5 text-xs font-semibold text-neutral-900">
              nicht am Stammplatz
            </span>
          )}
        </p>
        {!online && <p className="text-xs text-neutral-500">Ändern braucht Netz.</p>}
      </div>
      <div className="flex gap-2">
        {stammplatz !== null && (
          <Knopf art="still" disabled={!online} onClick={aufEntfernen}>
            entfernen
          </Knopf>
        )}
        <Knopf disabled={!online} onClick={aufAendern}>
          {stammplatz === null ? "festlegen" : "ändern"}
        </Knopf>
      </div>
    </div>
  );
}
